import {typeboxResolver} from '@hookform/resolvers/typebox'
import {announce} from '@react-aria/live-announcer'
import {type Static, Type} from '@sinclair/typebox'
import {TypeCompiler} from '@sinclair/typebox/compiler'
import {Value} from '@sinclair/typebox/value'
import {Button, FieldError, Form, Input, Label, TextField} from 'react-aria-components'
import {Controller, type SubmitHandler, useForm} from 'react-hook-form'

// import facebook2Svg from '@/assets/icons/social/facebook2.svg'
import googleSvg from '@/assets/icons/social/google.svg'
import decorationSvg from '@/assets/images/decoration.svg'
import LogoSvg from '@/assets/images/logo.svg?react'
import {LOGIN_DEFAULT_REDIRECT} from '@/auth/config'
import PasswordInput from '@/components/PasswordInput/PasswordInput'
import SkipLink from '@/components/SkipLink'
import Navbar from '@/layout/Navbar'
import HeadTags from '@/lib/head/HeadTags'
import isInApp from '@/lib/in-app/utils/isInApp'
import {useLocale} from '@/lib/locale/locale'
import getReferraId from '@/lib/referral/getReferralId'
import queries from '@/queries/queries'
import {HomeRoute, ResetPasswordRoute, SignInRoute, SignUpRoute} from '@/routeRegistry'
import {signIn} from '@/services/authenticationService'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import useClientValue from '@/utils/hooks/useClientValue'
import postMessageToReactNative from '@/utils/postMessageToReactNative'

import style from './SignIn.module.scss'

const schema = Type.Object({
  email: Type.String({
    format: 'email',
    default: '',
    errorMessage: 'Please enter a valid email address',
  }),
  password: Type.String({
    default: '',
    minLength: 1,
    errorMessage: "Password shouldn't be left empty",
  }),
})

const typecheck = TypeCompiler.Compile(schema)

type Schema = Static<typeof schema>

export default memo(function SignIn() {
  const {redirect} = SignInRoute.useSearch()
  const [isSigningIn, setIsSigningIn] = useState(false)
  const navigate = SignInRoute.useNavigate()
  const redirectTo = redirect ?? LOGIN_DEFAULT_REDIRECT
  const redirectFull = useClientValue(() => new URL(redirectTo, window.location.origin))
  const referrerId = getReferraId()
  const [locale] = useLocale()
  const queryClient = useQueryClient()

  const {handleSubmit, control, setError} = useForm<Schema>({
    mode: 'onTouched',
    resolver: typeboxResolver(typecheck),
    defaultValues: Value.Create(schema),
  })

  const onSubmit: SubmitHandler<Schema> = data => {
    setIsSigningIn(true)
    void signIn({...data, locale})
      .then(() => {
        void queryClient.invalidateQueries({
          queryKey: queries.getQueryKey('cart', [locale]),
          exact: true,
        })
        void navigate({
          to: redirectTo,
        })
      })
      .catch((error: Error) => {
        announce(error.message, 'assertive')
        setError(
          'password',
          {message: error.message},
          {
            shouldFocus: true,
          },
        )
      })
      .finally(() => {
        setIsSigningIn(false)
      })
  }

  const handleGoogleSignIn = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (isInApp()) {
      postMessageToReactNative({
        type: 'google-login',
        redirect: redirectTo,
        referrerId,
      })
    } else {
      window.location.href = `/api/auth/oauth2/authorization/google?origUrl=${
        redirectFull?.href ?? ''
      }${referrerId ? `&referrerId=${referrerId}` : ''}`
    }
  }

  return (
    <>
      <HeadTags title='Sign in' />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.SignInPage}>
        {/* <form className={style.Form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <div>
          <label htmlFor='email'>Email</label>
          <input
            id='email'
            aria-describedby='emailError emailHint'
            required
            aria-invalid={!!errors.email}
            {...register('email')}
          />
          {errors.email && (
            <span id='emailError' role='alert'>
              {errors.email.message}
            </span>
          )}
          <span id='emailHint'>Your current email address</span>
        </div>
        <div>
          <label htmlFor='password'>Password</label>
          <input
            id='password'
            aria-describedby='passwordError passwordHint'
            type='password'
            required
            aria-invalid={!!errors.password}
            {...register('password')}
          />
          {errors.password && <span role='alert'>{errors.password.message}</span>}
          <span id='passwordHint'>Should be at least 10 characters</span>
        </div>
        <input type='submit' />
      </form> */}
        <img src={decorationSvg} className={style.Decoration} alt='' />
        <div className={style.Content}>
          <Navbar withoutLogoOnDesktop transparentBg variant='dark' />
          <div className={style.Logo}>
            <Link to={HomeRoute.fullPath}>
              <LogoSvg title='InnoSearch Home' />
            </Link>
          </div>
          <main className={style.Main} {...skipTargetProps('main-content')}>
            <h1 className={style.Heading}>Sign in</h1>
            <div className={style.Helper}>
              New users?{' '}
              <Link to={SignUpRoute.fullPath} search={{redirect}}>
                Sign up
              </Link>{' '}
              for free
            </div>
            <Form className={style.Form} onSubmit={handleSubmit(onSubmit)}>
              <Controller
                control={control}
                name='email'
                render={({
                  field: {name, value, onChange, onBlur, ref},
                  fieldState: {invalid, error},
                }) => (
                  <TextField
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    validationBehavior='aria'
                    isInvalid={invalid}
                    isRequired
                    className={style.Field}
                  >
                    <Label className={style.Label}>Email</Label>
                    <Input className={style.Input} ref={ref} />
                    {!!error?.message && (
                      <FieldError className={style.Error}>{error.message}</FieldError>
                    )}
                  </TextField>
                )}
              />
              <Controller
                control={control}
                name='password'
                render={({
                  field: {name, value, onChange, onBlur, ref},
                  fieldState: {invalid, error},
                }) => (
                  <TextField
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    validationBehavior='aria'
                    isInvalid={invalid}
                    isRequired
                    className={style.Field}
                    type='password'
                  >
                    <Label className={style.Label}>Password</Label>
                    <PasswordInput ref={ref} className={clsx(style.Input, style.InputGroup)} />
                    {!!error?.message && (
                      <FieldError className={style.Error}>{error.message}</FieldError>
                    )}
                  </TextField>
                )}
              />
              <div className={style.ForgotPassword}>
                <Link to={ResetPasswordRoute.fullPath}>Forgot password?</Link>
              </div>
              <div className={style.SubmitArea}>
                <Button className={style.SignInBtn} type='submit' isDisabled={isSigningIn}>
                  {!isSigningIn ? 'Sign in' : 'Signing in...'}
                </Button>
                {!isInApp() && (
                  <div className={style.OAuth}>
                    {/* <a
                    href={
                      '/api/auth/oauth2/authorization/facebook?origUrl=' + redirectToLocation.href
                    }
                    className={style.OAuthItem}
                    aria-label='Sign in with Facebook'
                  >
                    <img src={facebook2Svg} alt='' />
                  </a> */}
                    {/* <div className={style.OAuthItem}>Apple</div> */}
                    <button
                      onClick={handleGoogleSignIn}
                      className={style.OAuthItem}
                      aria-label='Sign in with Google'
                      type='button'
                    >
                      <img src={googleSvg} alt='' />
                    </button>
                    {/* <div className={style.OAuthItem}>Twitter</div> */}
                  </div>
                )}
              </div>
            </Form>
          </main>
        </div>
      </div>
    </>
  )
})
